import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import { packerMyAudits, packerAuditItems, packerSaveAudit, packerSubmitAudit, packerEnterBarcode } from '../api/audit';

// Packer app → Audit tab. Cards are grouped by where each piece was when the
// audit started, so the packer can walk the warehouse bin by bin. Every tap is
// saved straight away (to the audit only — nothing touches inventory until
// staff close the audit), so leaving mid-way never loses work.
export default function PackerAudit({ onMsg }) {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({});   // item_id -> 'found' | 'not_found'
  const [busy, setBusy] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [barcodeMsg, setBarcodeMsg] = useState(null);   // { text } for not-in-audit / unknown
  const [searched, setSearched] = useState(null);       // piece card for a barcode in this audit
  const [saveError, setSaveError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setAudits(await packerMyAudits()); }
    catch (e) { /* leave empty */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function open(a) {
    setBusy(true); onMsg(null);
    try {
      const data = await packerAuditItems(a.audit_id);
      setItems(data.items || []);
      const ans = {};
      (data.items || []).forEach(it => { if (it.result) ans[it.item_id] = it.result; });
      setAnswers(ans); setActive(a); setSaveError(false);
    } catch (e) { onMsg({ err: true, text: e.response?.data?.detail || 'Could not open audit.' }); }
    finally { setBusy(false); }
  }

  async function answer(list) {
    // list: [{item_id, result}] — applied locally at once, then saved.
    setAnswers(prev => {
      const next = { ...prev };
      list.forEach(r => { if (r.result) next[r.item_id] = r.result; else delete next[r.item_id]; });
      return next;
    });
    try { await packerSaveAudit(active.audit_id, list); setSaveError(false); }
    catch (e) { setSaveError(true); }
  }

  function tap(itemId, val) {
    answer([{ item_id: itemId, result: answers[itemId] === val ? null : val }]);
  }

  function back() {
    setActive(null); setItems([]); setAnswers({}); setBarcode(''); setBarcodeMsg(null); setSearched(null);
    load();
  }

  async function saveAndExit() {
    // Re-send everything in case a tap failed to save.
    setBusy(true);
    try {
      await packerSaveAudit(active.audit_id,
        Object.entries(answers).map(([item_id, result]) => ({ item_id, result })));
      onMsg({ err: false, text: `Saved — ${Object.keys(answers).length} of ${items.length} answered. Come back any time.` });
      back();
    } catch (e) { setSaveError(true); }
    finally { setBusy(false); }
  }

  const allAnswered = items.length > 0 && items.every(it => answers[it.item_id]);

  async function submit() {
    if (!allAnswered) return;
    const nf = items.filter(it => answers[it.item_id] === 'not_found').length;
    if (!window.confirm(`Submit ${active.reference}? ${items.length - nf} found, ${nf} not found.`)) return;
    setBusy(true);
    try {
      await packerSubmitAudit(active.audit_id,
        items.map(it => ({ item_id: it.item_id, result: answers[it.item_id] })));
      onMsg({ err: false, text: `${active.reference} submitted — ${items.length - nf} found, ${nf} not found.` });
      back();
    } catch (e) { onMsg({ err: true, text: e.response?.data?.detail || 'Submit failed.' }); }
    finally { setBusy(false); }
  }

  // Full barcode only — exact match on the server, no suggestions. Searching
  // never ticks anything; the packer taps Mark found on the card it shows.
  async function enterBarcode(e) {
    e.preventDefault();
    const bc = barcode.trim();
    if (!bc) return;
    setBarcodeMsg(null); setSearched(null);
    try {
      const res = await packerEnterBarcode(active.audit_id, bc);
      if (res.result === 'in_audit') {
        setSearched(res.item);
        setBarcode('');
      } else if (res.result === 'not_in_audit') {
        setBarcodeMsg({ text: `${bc} — This piece is not in this audit` });
      } else {
        setBarcodeMsg({ text: `${bc} — Wrong barcode/Unknown piece` });
      }
    } catch (err) {
      setBarcodeMsg({ text: err.response?.data?.detail || 'Could not check — try again.' });
    }
  }

  const groups = useMemo(() => {
    const g = {};
    items.forEach(it => {
      if (onlyOpen && answers[it.item_id]) return;
      const k = it.location || '—';
      (g[k] = g[k] || []).push(it);
    });
    return Object.entries(g);
  }, [items, answers, onlyOpen]);

  if (!active) {
    return (
      <>
        <h2 className="text-xl font-bold text-gray-800 mb-1">Audit</h2>
        <p className="text-sm text-gray-400 mb-4">Find each piece and mark it found or not found. Your answers save as you go — you can leave and pick an order, then come back.</p>
        {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
        : audits.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No audits assigned to you right now.</div>
        : (
          <div className="space-y-2">
            {audits.map(a => (
              <button key={a.audit_id} onClick={() => open(a)} disabled={busy}
                className="w-full text-left bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-zoddle-green transition flex items-center justify-between disabled:opacity-50">
                <div>
                  <p className="font-semibold text-gray-800">{a.reference}</p>
                  <p className="text-xs text-gray-400">Started {a.created_at?.slice(0, 10)}</p>
                </div>
                <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {a.answered_count}/{a.item_count} done
                </span>
              </button>
            ))}
          </div>
        )}
      </>
    );
  }

  const answeredCount = Object.keys(answers).length;
  return (
    <>
      <button onClick={saveAndExit} className="text-sm text-gray-400 hover:text-gray-600 mb-3">← Save &amp; back to audits</button>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xl font-bold text-gray-800">{active.reference}</h2>
        <span className="text-sm font-semibold text-gray-500">{answeredCount}/{items.length} answered</span>
      </div>
      {saveError && (
        <div className="bg-pink-50 border border-pink-200 text-zoddle-pink text-sm rounded-lg px-4 py-2 mb-3">
          Couldn't save your last answer — check your connection, then tap Save &amp; exit.
        </div>
      )}
      <form onSubmit={enterBarcode} className="flex gap-2 mb-2">
        <input value={barcode} onChange={e => setBarcode(e.target.value)} placeholder="Type the full barcode"
          inputMode="numeric" autoComplete="off" autoCorrect="off" spellCheck={false} name="audit-barcode"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
        <button type="submit" disabled={!barcode.trim()}
          className="bg-zoddle-green text-white text-sm font-semibold px-4 rounded-lg disabled:opacity-50">Search</button>
      </form>
      {barcodeMsg && (
        <div className="rounded-lg px-3 py-2 mb-2 text-sm font-semibold bg-pink-50 text-zoddle-pink border border-pink-200 flex justify-between gap-2">
          <span>{barcodeMsg.text}</span>
          <button onClick={() => setBarcodeMsg(null)} className="text-gray-300 hover:text-gray-500">×</button>
        </div>
      )}
      {searched && (() => {
        const pick = answers[searched.item_id];
        return (
          <div className={`border-2 rounded-xl p-3 mb-2 bg-white flex gap-3 items-center ${pick === 'found' ? 'border-zoddle-green' : 'border-zoddle-blue'}`}>
            {searched.image_url_1
              ? <img src={cldThumb(searched.image_url_1, 200)} alt={searched.design_name} className="w-20 h-20 object-cover rounded-lg shrink-0 bg-gray-100" />
              : <div className="w-20 h-20 bg-gray-100 rounded-lg shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate"><span className="font-semibold text-gray-800">{searched.brand_name || '—'}</span><span className="text-gray-500"> · {searched.size}</span></p>
              <p className="text-xs text-gray-400 font-mono">{searched.zoddle_barcode}</p>
              <p className="text-xs text-gray-500 mt-0.5 truncate">{searched.location}</p>
              {pick === 'found'
                ? <p className="text-xs font-bold text-zoddle-green mt-2">✓ Marked found</p>
                : <button onClick={() => answer([{ item_id: searched.item_id, result: 'found' }])}
                    className="mt-2 bg-zoddle-green text-white text-sm font-bold px-4 py-1.5 rounded-lg">Mark found</button>}
              {pick === 'not_found' && <p className="text-[11px] text-zoddle-pink mt-1">Currently marked not found — Mark found will change it.</p>}
            </div>
            <button onClick={() => setSearched(null)} className="text-gray-300 hover:text-gray-500 text-lg self-start">×</button>
          </div>
        );
      })()}
      <div className="flex justify-end mb-4">
        <label className="flex items-center gap-1.5 text-sm text-gray-600 shrink-0">
          <input type="checkbox" checked={onlyOpen} onChange={e => setOnlyOpen(e.target.checked)} />
          Unanswered only
        </label>
      </div>

      <div className="mb-24 space-y-5">
        {groups.length === 0 && <div className="text-center py-10 text-gray-400 text-sm">Nothing to show.</div>}
        {groups.map(([loc, list]) => {
          const open = list.filter(it => !answers[it.item_id]);
          return (
            <div key={loc}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-700">{loc} <span className="text-gray-400 font-normal">· {list.length}</span></h3>
                {open.length > 0 && (
                  <button onClick={() => {
                      if (window.confirm(`Mark the ${open.length} unanswered piece(s) in "${loc}" as not found?`))
                        answer(open.map(it => ({ item_id: it.item_id, result: 'not_found' })));
                    }}
                    className="text-xs text-zoddle-pink font-semibold">Rest not found</button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {list.map(it => {
                  const pick = answers[it.item_id];
                  return (
                    <div key={it.item_id} className={`border-2 rounded-xl p-2 bg-white transition ${pick === 'found' ? 'border-zoddle-green' : pick === 'not_found' ? 'border-zoddle-pink' : 'border-gray-200'}`}>
                      {it.image_url_1
                        ? <img src={cldThumb(it.image_url_1, 300)} alt={it.design_name} className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100" loading="lazy" />
                        : <div className="w-full aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
                      <p className="text-xs truncate"><span className="font-semibold text-gray-800">{it.brand_name || '—'}</span><span className="text-gray-500"> · {it.size}</span></p>
                      <p className="text-xs text-gray-400 truncate mb-2 font-mono">{it.zoddle_barcode}</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        <button onClick={() => tap(it.item_id, 'found')}
                          className={`text-xs font-semibold py-1.5 rounded-lg border transition ${pick === 'found' ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>Found</button>
                        <button onClick={() => tap(it.item_id, 'not_found')}
                          className={`text-xs font-semibold py-1.5 rounded-lg border transition ${pick === 'not_found' ? 'bg-zoddle-pink text-white border-zoddle-pink' : 'border-gray-200 text-gray-500'}`}>Not found</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-5 py-3">
        <div className="max-w-3xl mx-auto grid grid-cols-2 gap-2">
          <button onClick={saveAndExit} disabled={busy}
            className="border border-gray-300 text-gray-700 text-sm font-semibold py-3 rounded-lg disabled:opacity-50">
            Save &amp; exit
          </button>
          <button onClick={submit} disabled={!allAnswered || busy}
            className="bg-zoddle-green text-white text-sm font-semibold py-3 rounded-lg disabled:opacity-50">
            {allAnswered ? 'Submit' : `${items.length - answeredCount} left`}
          </button>
        </div>
      </div>
    </>
  );
}
